import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Images from "../../assets";

const formatCurrency = (value, locale = "el-GR", currency = "EUR") => {
    const num = Number(value || 0);

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
    }).format(num);
};

const formatDate = (dateString, locale = "el-GR") => {
    if (!dateString) return "-";

    const normalized = String(dateString).replace(" ", "T");
    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return dateString;

    return new Intl.DateTimeFormat(locale).format(date);
};

const buildAddressText = ({
                              name,
                              company,
                              address,
                              zipCode,
                              city,
                              region,
                              country,
                              phone,
                              phone2,
                              email,
                          }) => {
    return [
        name,
        company,
        address,
        [zipCode, city].filter(Boolean).join(" "),
        region,
        country,
        phone,
        phone2,
        email,
    ]
        .filter(Boolean)
        .join("\n");
};

const getLocalizedItemName = (item, language) => {
    if (language === "en") {
        return item?.nameEN || item?.nameEL || "-";
    }

    return item?.nameEL || item?.nameEN || "-";
};

const getLocalizedAddressValue = (obj, baseKey, language) => {
    if (!obj) return "";

    if (language === "en") {
        return obj?.[`${baseKey}EN`] || obj?.[baseKey] || "";
    }

    return obj?.[baseKey] || obj?.[`${baseKey}EN`] || "";
};

export const useOrderConfirmation = ({
                                         finalResponse,
                                         language = "el",
                                         paymentOptions = {},
                                     }) => {
    const { t } = useTranslation();

    return useMemo(() => {
        const data = finalResponse?.data ?? {};
        const order = data?.order ?? {};
        const items = data?.items ?? [];
        const address = data?.address ?? {};
        const carrier = data?.carrier ?? {};

        const locale = language === "en" ? "en-GB" : "el-GR";

        const defaultPaymentMap = {
            "1": t("orderConfirmation.paymentMethods.card", "Πιστωτική κάρτα"),
            "2": t("orderConfirmation.paymentMethods.cash", "Αντικαταβολή"),
            "3": t("orderConfirmation.paymentMethods.bank", "Τραπεζική κατάθεση"),
        };

        const mergedPaymentMap = {
            ...defaultPaymentMap,
            ...paymentOptions,
        };

        const getPaymentLabel = (paymentId) =>
            mergedPaymentMap[paymentId] || paymentId || "-";

        const orderHeader = {
            orderNumber: order?.orderNumber || "-",
            orderDate: formatDate(order?.createDt, locale),
            orderTotal: `${formatCurrency(order?.total, locale)} (${items.length} ${t(
                "orderConfirmation.itemsCount",
                "items"
            )})`,
            paymentMethod: getPaymentLabel(order?.payment),
        };

        const orderItems = items.map((item, index) => ({
            id: item?.id || index,
            imageUrl: item?.image || Images.product1,
            quantityLabel: `${item?.quantity || 0}x`,
            sku: `${t("orderConfirmation.skuLabel", "ΚΩΔΙΚΟΣ")}: ${item?.code || "-"}`,
            name: getLocalizedItemName(item, language),
            price: formatCurrency(item?.price, locale),
            // price: formatCurrency(
            //     Number(item?.price || 0) * Number(item?.quantity || 0),
            //     locale
            // ),
        }));

        const orderTotals = [
            {
                key: "subtotal",
                label: t("orderConfirmation.totals.subtotal", "Καθαρή Αξία"),
                value: formatCurrency(order?.subtotal, locale),
                isGrandTotal: false,
            },
            {
                key: "tax",
                label: t("orderConfirmation.totals.tax", "ΦΠΑ"),
                value: formatCurrency(order?.tax, locale),
                isGrandTotal: false,
            },
            {
                key: "shipping",
                label: t("orderConfirmation.totals.shipping", "Έξοδα Αποστολής"),
                value: formatCurrency(order?.shipping, locale),
                isGrandTotal: false,
            },
            {
                key: "payment",
                label: t("orderConfirmation.totals.payment", "Τρόπος Πληρωμής"),
                value: getPaymentLabel(order?.payment),
                isGrandTotal: false,
            },
            {
                key: "total",
                label: t("orderConfirmation.totals.total", "Τελικό Σύνολο"),
                value: formatCurrency(order?.total, locale),
                isGrandTotal: true,
            },
        ];

        const additionalInfoText =
            address?.Comments ||
            order?.comment ||
            t("orderConfirmation.noAdditionalInfo", "-");

        const countryLabel =
            language === "en" ? order?.Country || "" : order?.Country || "";

        const deliveryDetailsText = buildAddressText({
            name: address?.Name || address?.FullName || "",
            company: address?.Company || "",
            address: getLocalizedAddressValue(address, "Address", language),
            zipCode: getLocalizedAddressValue(address, "ZipCode", language),
            city: getLocalizedAddressValue(address, "City", language),
            region: getLocalizedAddressValue(address, "Region", language),
            country: countryLabel,
            phone: address?.AddrPhone,
            phone2: address?.AddrPhone2,
            email: address?.Email,
        });

        const billingDetailsText = buildAddressText({
            name: address?.Name || address?.FullName || "",
            company: address?.Company || "",
            address: getLocalizedAddressValue(address, "Address", language),
            zipCode: getLocalizedAddressValue(address, "ZipCode", language),
            city: getLocalizedAddressValue(address, "City", language),
            region: getLocalizedAddressValue(address, "Region", language),
            country: countryLabel,
            phone: address?.AddrPhone,
            phone2: address?.AddrPhone2,
            email: address?.Email,
        });

        const shippingDetailsText = buildAddressText({
            name: carrier?.ucName,
            company: "",
            address: carrier?.ucAddress,
            zipCode: carrier?.ucZipCode,
            city: carrier?.ucCity,
            region: "",
            country: "",
            phone: carrier?.ucPhone,
            phone2: "",
            email: carrier?.ucEmail,
        });

        return {
            orderHeader,
            orderItems,
            orderTotals,
            additionalInfoText,
            deliveryDetailsText,
            billingDetailsText,
            shippingDetailsText,
            raw: {
                order,
                items,
                address,
                carrier,
            },
        };
    }, [finalResponse, language, paymentOptions, t]);
};